import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Indonesian mirror of the power-of-attorney guide. */
const TITLE = "Surat kuasa dari Thailand untuk dipakai di luar negeri — cara menyusun agar tidak ditolak";
const DESC =
  "Struktur surat kuasa, cakupan kewenangan yang perlu ditulis, urutan notarisasi dan pengesahan, serta kesalahan yang membuat dokumen harus dibuat ulang.";
const PATH = "/blog/power-of-attorney-abroad-guide";
const URL = `/id${PATH}`;
const PUBLISHED = "2026-08-11";

const FAQ = [
  {
    question: "Apakah surat kuasa harus dinotarisasi?",
    answer:
      "Surat kuasa adalah dokumen yang dibuat sendiri, sehingga instansi penerima umumnya meminta advokat notarial mengesahkan tanda tangan pemberi kuasa sebelum pengesahan lanjutan.",
  },
  {
    question: "Bahasa apa yang sebaiknya dipakai?",
    answer:
      "Gunakan bahasa yang diminta instansi penerima. Versi dua bahasa Thai–Inggris sering mempermudah, dengan syarat kedua versi identik isinya.",
  },
  {
    question: "Seberapa rinci cakupan kewenangan harus ditulis?",
    answer:
      "Sebutkan tindakan spesifik: menandatangani, mengambil, menyerahkan, membayar, atau mewakili pada instansi tertentu. Kewenangan yang terlalu umum sering ditolak.",
  },
  {
    question: "Bagaimana urutan pengesahannya?",
    answer:
      "Notarisasi tanda tangan oleh advokat notarial, pengesahan Departemen Konsuler, penerjemahan bila perlu, lalu legalisasi kedutaan negara tujuan bila diminta.",
  },
  {
    question: "Apakah surat kuasa perlu saksi?",
    answer:
      "Banyak instansi meminta satu atau dua saksi dengan identitas jelas. Periksa formatnya lebih dulu karena persyaratan berbeda per negara.",
  },
  {
    question: "Apakah surat kuasa ada masa berlaku?",
    answer:
      "Anda dapat mencantumkan tanggal berakhir. Bila tidak dicantumkan, instansi penerima sering membatasi usia dokumen sejak tanggal notarisasi.",
  },
  {
    question: "Apa kesalahan yang paling sering terjadi?",
    answer:
      "Nama tidak sama dengan paspor, cakupan kewenangan kurang lengkap, tanda tangan dibuat sebelum bertemu advokat, dan urutan pengesahan tertukar.",
  },
];

export const Route = createFileRoute("/id/blog/power-of-attorney-abroad-guide")({
  component: IdPoaGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "id" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "id") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "surat kuasa Thailand, notarisasi surat kuasa Thailand, legalisasi surat kuasa, power of attorney Thailand",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "id"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "Beranda", url: "/id" },
              { name: "Panduan", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const PARTS: Array<{ d: string; note: string }> = [
  { d: "Identitas pemberi kuasa", note: "Nama sesuai paspor, nomor identitas, dan alamat" },
  { d: "Identitas penerima kuasa", note: "Nama lengkap dan hubungan atau jabatan" },
  { d: "Cakupan kewenangan", note: "Daftar tindakan spesifik, bukan rumusan umum" },
  { d: "Objek atau perkara", note: "Nomor rekening, aset, atau nomor berkas yang jelas" },
  { d: "Masa berlaku", note: "Tanggal mulai dan berakhir bila diperlukan" },
  { d: "Tanda tangan dan saksi", note: "Ditandatangani di hadapan advokat notarial" },
];

const ORDER: string[] = [
  "Minta format atau daftar resmi dari instansi penerima",
  "Susun teks dengan cakupan kewenangan yang spesifik",
  "Tanda tangan di hadapan advokat notarial untuk notarisasi",
  "Ajukan pengesahan ke Departemen Konsuler Kementerian Luar Negeri Thailand",
  "Terjemahkan lengkap termasuk stempel dan catatan kaki",
  "Legalisasi kedutaan negara tujuan bila diminta, lalu periksa ulang nama dan tanggal",
];

const MISTAKES: string[] = [
  "Menandatangani dokumen sebelum bertemu advokat notarial",
  "Cakupan kewenangan tidak mencakup tindakan yang benar-benar diperlukan",
  "Ejaan nama berbeda antara surat kuasa, paspor, dan terjemahan",
  "Terjemahan tidak memuat stempel dan segel",
  "Melakukan penerjemahan sebelum pengesahan yang seharusnya lebih dulu",
];

function IdPoaGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/id" className="text-muted-foreground hover:text-foreground">
          Beranda
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          Panduan
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="id" />
      <p className="answer-summary speakable-summary">
        Surat kuasa termasuk dokumen yang dibuat sendiri, jadi keabsahannya di luar negeri bergantung pada notarisasi
        tanda tangan oleh advokat notarial, cakupan kewenangan yang spesifik, lalu pengesahan Departemen Konsuler dan
        legalisasi kedutaan bila instansi penerima memintanya.
      </p>

      <h2>1. Bagian yang harus ada</h2>
      <ul>
        {PARTS.map((x) => (
          <li key={x.d}>
            <strong>{x.d}:</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>2. Urutan pengerjaan</h2>
      <ol>
        {ORDER.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. Kesalahan yang sering terjadi</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. Pertanyaan yang sering diajukan</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. Sumber resmi dan layanan terkait</h2>
      <p className="text-sm">
        Referensi:{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          Lawyers Council of Thailand
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          Departemen Konsuler, Kementerian Luar Negeri Thailand
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/id/notary" className="text-primary underline">
            Notarisasi oleh advokat Notarial Services Attorney
          </Link>
        </li>
        <li>
          <Link to="/id/lgz" className="text-primary underline">
            Pengesahan dan legalisasi dokumen
          </Link>
        </li>
        <li>
          <Link to="/id/corporate" className="text-primary underline">
            Layanan dokumen perusahaan
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Konten ditinjau pada {PUBLISHED}. Ini informasi umum, bukan jaminan atas keputusan instansi mana pun. Bila Anda
        ingin surat kuasa disusun dan diproses dengan benar sejak awal, tim kami dengan pengalaman lebih dari 15 tahun
        siap memberi konsultasi dan menangani seluruh langkah — hubungi kami melalui telepon, LINE, atau email.
      </p>
    </article>
  );
}
